import type { IStyleParam } from '@wix/tpa-settings';
import { useStyles } from '@wix/tpa-settings/react';
import { ImageResizeOptions } from 'wix-ui-tpa/cssVars';
import { Add } from '@wix/wix-ui-icons-common';
import { Cart, EcomCart, ShoppingBag, Stores } from '@wix/wix-ui-icons-common/on-stage';
import type { FeaturedBadgePlacement } from 'root/consts';
import { stylesParams } from 'root/components/MenuAddOn/stylesParams';
import {
  AddToCartButtonContent,
  AddToCartButtonIconType,
  AddToCartButtonWidth,
} from 'root/components/MenuAddOn/Settings/Tabs/DesignTab/AddToCartButtonDesign/consts';
import {
  DEFAULT_FEATURED_BADGE_PLACEMENT,
  DEFAULT_ITEM_IMAGE_RESIZE_MODE,
  DEFAULT_ITEMS_COLUMN_GAP,
  DEFAULT_ITEMS_PER_LOAD,
  DEFAULT_MAX_ITEMS_PER_ROW,
  ITEMS_PER_LOAD_MAX,
  ITEMS_PER_LOAD_MIN,
  MENU_ADD_ON_SPACING_MAX,
  MENU_ADD_ON_SPACING_MIN,
  MAX_ITEMS_PER_ROW_MAX,
  MAX_ITEMS_PER_ROW_MIN,
  MenuAddOnImageResizeMode,
  MenuAddOnItemsDisplayMode,
  clampStyleNumber,
} from 'root/components/MenuAddOn/Settings/Tabs/LayoutTab/consts';

const ADD_TO_CART_ICONS = [Cart, EcomCart, Stores, ShoppingBag, Add] as const;

const getImageResizeOption = (resizeMode: MenuAddOnImageResizeMode) =>
  resizeMode === MenuAddOnImageResizeMode.Fit ? ImageResizeOptions.contain : ImageResizeOptions.cover;

export const useMenuAddOnWidgetStyles = () => {
  const styles = useStyles();
  const getStyleBool = (key: string): boolean => {
    const param = (stylesParams as unknown as Record<string, IStyleParam>)[key];
    return param ? (styles.get(param) as boolean) : true;
  };

  const showTitle = getStyleBool('showMenuName');
  const showSubtitle = getStyleBool('showMenuDescription');
  const showItemImage = getStyleBool('showItemImage');
  const maxItemsPerRow = clampStyleNumber(
    styles.get(stylesParams.menuAddOnMaxItemsPerRow),
    DEFAULT_MAX_ITEMS_PER_ROW,
    MAX_ITEMS_PER_ROW_MIN,
    MAX_ITEMS_PER_ROW_MAX
  );
  const itemsDisplayMode = styles.get(stylesParams.menuAddOnItemsDisplayMode) as MenuAddOnItemsDisplayMode;
  const itemsPerLoad = clampStyleNumber(
    styles.get(stylesParams.menuAddOnItemsPerLoad),
    DEFAULT_ITEMS_PER_LOAD,
    ITEMS_PER_LOAD_MIN,
    ITEMS_PER_LOAD_MAX
  );
  const itemsColumnGap = clampStyleNumber(
    styles.get(stylesParams.menuAddOnItemsColumnGap),
    DEFAULT_ITEMS_COLUMN_GAP,
    MENU_ADD_ON_SPACING_MIN,
    MENU_ADD_ON_SPACING_MAX
  );
  const itemImageResizeMode =
    (styles.get(stylesParams.menuAddOnItemImageResizeMode) as MenuAddOnImageResizeMode) ??
    DEFAULT_ITEM_IMAGE_RESIZE_MODE;
  const featuredBadgePlacement =
    (styles.get(stylesParams.menuAddOnFeaturedBadgePlacement) as FeaturedBadgePlacement) ??
    DEFAULT_FEATURED_BADGE_PLACEMENT;
  const addToCartButtonContent = styles.get(stylesParams.menuAddOnAddToCartButtonContent) as AddToCartButtonContent;
  const addToCartButtonIconType = clampStyleNumber(
    styles.get(stylesParams.menuAddOnAddToCartButtonIconType),
    AddToCartButtonIconType.Add,
    AddToCartButtonIconType.Cart,
    AddToCartButtonIconType.Add
  );
  const addToCartButtonFullWidth =
    (styles.get(stylesParams.menuAddOnAddToCartButtonWidth) as AddToCartButtonWidth) !== AddToCartButtonWidth.FitToText;
  const isSliderItemsLayout = itemsDisplayMode === MenuAddOnItemsDisplayMode.Slider;

  const AddToCartIcon = ADD_TO_CART_ICONS[addToCartButtonIconType] ?? Add;
  const shouldShowAddToCartIcon = addToCartButtonContent !== AddToCartButtonContent.TextOnly;
  const shouldShowAddToCartLabel = addToCartButtonContent !== AddToCartButtonContent.IconOnly;

  return {
    AddToCartIcon,
    addToCartButtonFullWidth,
    featuredBadgePlacement,
    isSliderItemsLayout,
    itemImageResize: getImageResizeOption(itemImageResizeMode),
    itemsColumnGap,
    itemsPerLoad,
    maxItemsPerRow,
    shouldShowAddToCartIcon,
    shouldShowAddToCartLabel,
    showItemImage,
    showSubtitle,
    showTitle,
  };
};
